import React from "react";
import { useSelector } from "react-redux";
import SingleSelect from "./SingleSelect";
import { checkUserDetailsFromCookie } from "../../../api_calls/userManagement";
import { CROSS_COMPANY_EDIT_USER_TYPES } from "../../../api_calls/crossCompanyAccess";

/** User types that may choose a company owner on create. Same list as cross-company edit. */
export const OWNER_SELECTABLE_USER_TYPES = CROSS_COMPANY_EDIT_USER_TYPES;

/**
 * Resolve the default owner option for create forms.
 * - Exactly one company → that option (field stays hidden; still write for save).
 * - Multiple companies → Cyreen match (field is shown for the user to change).
 * - Otherwise → undefined.
 */
export const getDefaultOwnerOption = (ownersList) => {
  if (typeof ownersList === "undefined" || ownersList === null) {
    return undefined;
  }

  const options = Object.values(ownersList);

  if (options.length === 1) {
    return options[0];
  }

  if (options.length > 1) {
    return options.find((owner) => owner.label.includes("Cyreen"));
  }

  return undefined;
};

/**
 * Flat company-owner dropdown (kept for later reuse).
 * Campaign / clip / loop create now use CompanyHierarchySingleSelect instead.
 *
 * Presentational only:
 * - Shown only for cyreen/tenant on create when more than one owner company exists.
 * - Options come from Redux `independent_owners_list`.
 * - Callers own field writes, clears, and side effects via `onChangeCall`.
 */
const OwnerField = ({
  usedFor,
  defaultVal = "",
  fieldError = "",
  onChangeCall,
  label = "Owner*",
  elemId = "ownerDropdown",
}) => {
  const independentOwnersList = useSelector(
    (state) => state.userSelections.independent_owners_list
  );

  const checkUserDetails = checkUserDetailsFromCookie();
  const userType =
    checkUserDetails.success === true
      ? checkUserDetails.user_detail.user_type
      : "";

  const options =
    typeof independentOwnersList !== "undefined"
      ? Object.values(independentOwnersList)
      : [];

  // Selectable only when creating, user can own as cyreen/tenant, and there is a real choice.
  if (
    !OWNER_SELECTABLE_USER_TYPES.includes(userType) ||
    usedFor === "update" ||
    options.length <= 1
  ) {
    return null;
  }

  return (
    <SingleSelect
      label={label}
      enableFullWidth={true}
      elemId={elemId}
      options={options}
      selectionCheck={(option, value) => option?.id === value?.id}
      defaultVal={defaultVal}
      onChangeCall={onChangeCall}
      fieldError={fieldError}
    />
  );
};

export default OwnerField;
